// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { createFragmentContainer } from "react-relay";
import withStyles from "@material-ui/core/styles/withStyles";

// components
import { AvatarPerson } from "@components/avatar/AvatarPerson";

// styles
import { greys } from "@clockwise/web-commons/src/styles/color.styles";

// internal
import { IProps, IState } from "./AvatarRow.d";
import { styles } from "./AvatarRow.styles";
import { fragments } from "./AvatarRow.gql";
import Tooltip from "@material-ui/core/Tooltip";
import { niceNameFromPerson } from "@clockwise/client-commons/src/util/event";

class AvatarRowComponent extends React.Component<IProps, IState> {
  static defaultProps = {
    extraOverflowCount: 0,
    maxAvatarCount: 3,
    size: 24,
    overlap: 6,
  };

  constructor(props: IProps) {
    super(props);

    this.state = {};
  }

  ///////////////
  // Rendering //
  ///////////////
  private getTooltipTitle = (
    persons: (ISchema.IPerson | ISchema.IOrgPerson)[],
    overflowCount?: number,
  ) => {
    const tooltipTitleItems = persons.map(
      (person, i) =>
        `${niceNameFromPerson(person, true, undefined, true)}${
          i !== persons.length - 1 ? "," : ""
        }`,
    );

    if (overflowCount) {
      tooltipTitleItems.push(`${persons.length ? "and " : "+"}${overflowCount} others`);
    }

    return tooltipTitleItems.join(" ");
  };

  private renderAvatarPerson = (
    person: ISchema.IPerson | null,
    orgPerson: ISchema.IOrgPerson | null,
    i: number,
    length: number,
  ) => {
    const { classes, size, overlap, org, showTooltip } = this.props;

    return (
      <div
        className={classes.avatar}
        style={{ zIndex: length - i, width: size - overlap }}
        key={`AvatarRow-AvatarPerson-${i}`}
      >
        <Tooltip
          open={!showTooltip ? false : undefined}
          title={
            <div>
              <div>{niceNameFromPerson((orgPerson || person)!, true, undefined, true)}</div>
              <div>
                {(orgPerson && orgPerson.primaryCalendarId) || (person && person.primaryEmail)}
              </div>
            </div>
          }
        >
          <div>
            <AvatarPerson
              org={org}
              style={{ border: `2px solid ${greys.white}`, fontSize: Math.floor(size / 3) }}
              orgPerson={orgPerson}
              person={person}
              backupCalendarId={
                (orgPerson && orgPerson.primaryCalendarId) || (person && person.primaryEmail) || ""
              }
              size={size}
              parentName="OnboardingTeamJoinCreateStep"
              hideUserBadge
              unClickable
            />
          </div>
        </Tooltip>
      </div>
    );
  };

  public render() {
    const {
      orgPersons,
      persons,
      extraOverflowCount,
      maxAvatarCount,
      size,
      classes,
      showTooltip,
    } = this.props;

    let overflowCount = extraOverflowCount;

    if (orgPersons) {
      const overflowPersons: ISchema.IOrgPerson[] = [];

      const filteredPersons = orgPersons.filter((person, i) => {
        if (person && i < maxAvatarCount) {
          return true;
        }

        overflowPersons.push(person);

        overflowCount++;
        return false;
      });

      const adjustedOverflowCount = overflowCount - overflowPersons.length;

      return (
        <React.Fragment>
          {filteredPersons.map((person, i) =>
            this.renderAvatarPerson(null, person, i, filteredPersons.length),
          )}
          {overflowCount ? (
            <Tooltip
              open={!showTooltip ? false : undefined}
              title={this.getTooltipTitle(overflowPersons, adjustedOverflowCount)}
            >
              {
                <div className={classes.avatar}>
                  <div
                    className="overflow"
                    style={{
                      width: size,
                      height: size,
                      fontSize: size / (3 + Math.floor(Math.log10(overflowCount))),
                    }}
                  >
                    +{overflowCount}
                  </div>
                </div>
              }
            </Tooltip>
          ) : null}
        </React.Fragment>
      );
    }

    if (persons) {
      const overflowPersons: ISchema.IPerson[] = [];

      const filteredPersons = persons.filter((person, i) => {
        if (person && i < maxAvatarCount) {
          return true;
        }

        overflowPersons.push(person);

        overflowCount++;
        return false;
      });

      const adjustedOverflowCount = overflowCount - overflowPersons.length;

      return (
        <React.Fragment>
          {filteredPersons.map((person, i) =>
            this.renderAvatarPerson(person, null, i, filteredPersons.length),
          )}
          {overflowCount ? (
            <Tooltip
              open={!showTooltip ? false : undefined}
              title={this.getTooltipTitle(overflowPersons, adjustedOverflowCount)}
            >
              {
                <div className={classes.avatar}>
                  <div
                    className="overflow"
                    style={{
                      width: size,
                      height: size,
                      fontSize: size / (3 + Math.floor(Math.log10(overflowCount))),
                    }}
                  >
                    +{overflowCount}
                  </div>
                </div>
              }
            </Tooltip>
          ) : null}
        </React.Fragment>
      );
    }

    return null;
  }
}

/////////////////
// With Styles //
/////////////////

export const AvatarRowWithStyles = withStyles(styles)(AvatarRowComponent);

///////////
// Relay //
///////////

export const AvatarRow = createFragmentContainer(AvatarRowWithStyles, fragments);
